/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useCallback, useState } from "react";
import receiptsUploadStyle from "./receiptsUploadStyle";
import Paper from "@mui/material/Paper";
import ButtonLoading from "../../../Button/ButtonLoading";
import TextContainer from "../../../Container/TextContainer";
import _reduce from "lodash/reduce";
import _isArrayLike from "lodash/isArrayLike";
import { toUserInputtedFloat } from "../../../../services/formServiceFunctions";
import Grid from "@mui/material/Grid";
import FileUploadSection from "../FileUploadSection/FileUploadSection";
import { useForm } from "../../../../provider/Form/FormProvider";
import Signature from "../../Common/Signature/Signature";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";
import FormSubmitProps from "../../../../types/FormSubmitProps";
import { Box } from "@mui/material";

type ReceiptsUploadProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

export default function ReceiptsUpload({ product, submitProps }: ReceiptsUploadProps) {
  const { handleSubmit } = useForm();
  const [isBlocked, setIsBlocked] = useState(false);
  const hasReceipt = useCallback(
    (receiptKey: string) => {
      return _isArrayLike(product.receipts[receiptKey]) && product.receipts[receiptKey].length > 0;
    },
    [product.receipts]
  );

  const isComplete = useCallback(() => {
    const processCostSupport = product;
    if (!processCostSupport.petitioner.signature) {
      return false;
    }

    if (processCostSupport.isCostCovered && !hasReceipt("pcs_receipt_b")) {
      return false;
    }

    if (processCostSupport.hasAlimonyClaims && !hasReceipt("pcs_receipt_c")) {
      return false;
    }

    if (processCostSupport.hasRelativesAlimonyClaims && alimonyClaimsAmount() > 0 && !hasReceipt("pcs_receipt_d")) {
      return false;
    }

    if (processCostSupport.hasSocialWelfare && !hasReceipt("pcs_receipt_social_welfare")) {
      return false;
    }

    if (processCostSupport.hasIncomeEmployment && !hasReceipt("pcs_receipt_e1")) {
      return false;
    }

    if (processCostSupport.hasIncomeSelfEmployment && !hasReceipt("pcs_receipt_e2")) {
      return false;
    }

    if (processCostSupport.hasIncomeRent && !hasReceipt("pcs_receipt_e3")) {
      return false;
    }

    if (processCostSupport.hasIncomeAssets && !hasReceipt("pcs_receipt_e4")) {
      return false;
    }

    if (processCostSupport.hasIncomeLiving && !hasReceipt("pcs_eceipt_e6")) {
      return false;
    }

    if (processCostSupport.hasIncomePension && !hasReceipt("pcs_receipt_e8")) {
      return false;
    }

    if (processCostSupport.hasIncomeUnemploymentAllowance && !hasReceipt("pcs_receipt_e9")) {
      return false;
    }

    if (processCostSupport.hasIncomeUnemploymentAllowance2 && !hasReceipt("pcs_receipt_e10")) {
      return false;
    }

    if (processCostSupport.hasIncomeSickness && !hasReceipt("pcs_receipt_e11")) {
      return false;
    }

    if (processCostSupport.hasIncomeParentalAllowance && !hasReceipt("pcs_receipt_e12")) {
      return false;
    }

    if (processCostSupport.hasIncomeOther1 && !hasReceipt("pcs_receipt_e13")) {
      return false;
    }

    if (processCostSupport.hasIncomeOther2 && !hasReceipt("pcs_receipt_e14")) {
      return false;
    }

    if (processCostSupport.hasOtherInsuranceDeduction && !hasReceipt("pcs_receipt_f3")) {
      return false;
    }

    if (processCostSupport.hasMobilityCostDeduction && processCostSupport.mobilityCostUnit !== "km" && !hasReceipt("pcs_receipt_f4")) {
      return false;
    }

    if (processCostSupport.hasOtherExpensesDeduction && !hasReceipt("pcs_receipt_f5")) {
      return false;
    }

    if (processCostSupport.hasIncomeLiving === false && !hasReceipt("pcs_receipt_h1")) {
      return false;
    }

    if (processCostSupport.hasPensionInsurance && !hasReceipt("pcs_receipt_g5")) {
      return false;
    }

    if (processCostSupport.hasOtherAssets && !hasReceipt("pcs_receipt_g6")) {
      return false;
    }

    if (processCostSupport.hasPropertyLoans) {
      for (let i = 1; i <= processCostSupport.propertyLoans.length; i++) {
        if (!hasReceipt("pcs_receipt_h6" + i)) {
          return false;
        }
      }
    }

    if (processCostSupport.hasOtherPaymentObligations) {
      for (let i = 1; i <= processCostSupport.paymentObligations.length; i++) {
        if (!hasReceipt("pcs_receipt_i" + i)) {
          return false;
        }
      }
    }

    if (processCostSupport.hasSpecialFinancialBurdens) {
      for (let i = 1; i <= processCostSupport.specialFinancialBurdens.length; i++) {
        if (!hasReceipt("pcs_receipt_j" + i)) {
          return false;
        }
      }
    }

    if (processCostSupport.hasBankAccount) {
      for (let i = 1; i <= processCostSupport.bankAccounts.length; i++) {
        if (!hasReceipt("pcs_receipt_g1" + i)) {
          return false;
        }
      }
    }

    return true;
  }, [product]);

  const alimonyClaimsAmount = useCallback(() => {
    return _reduce(
      product.alimonyPersons,
      (sum, person) => {
        return sum + (toUserInputtedFloat(person.alimony) ?? 0);
      },
      0
    );
  }, [product.alimonyPersons]);

  let counter = 1;
  return (
    <Paper sx={receiptsUploadStyle.paper} elevation={4}>
      <TextContainer>
        <Box sx={receiptsUploadStyle.inlineHintBox}>
          <Typography variant={"h4"}>Benötigte Unterlagen</Typography>
          Laden Sie hier Ihre Belege als Bild oder PDF hoch, oder fotografieren Sie diese einfach ab. Achten Sie dabei unbedingt darauf, dass alles vollständig
          und gut lesbar ist, da diese Belege so beim Gericht eingereicht werden und entscheidend dafür sind, dass Ihnen Verfahrenskostenhilfe gewährt wird.
        </Box>

        {product.isCostCovered && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Rechtsschutzversicherung"}
            fieldname={"pcs_receipt_b"}
            description={"B"}
            helperText={"Fügen Sie hier den Versicherungsschein Ihrer Rechtsschutzversicherung ein"}
            processCostSupport={product}
          />
        )}

        {product.hasAlimonyClaims && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Unterhaltsanspruch"}
            fieldname={"pcs_receipt_c"}
            description={"C"}
            helperText={
              "Bitte laden Sie einen Kontoauszug hoch, auf dem der Eingang der Unterhaltszahlung an Sie zu sehen ist, oder einen Beleg darüber, dass Ihnen Zahlungen zustehen."
            }
            processCostSupport={product}
          />
        )}

        {product.hasRelativesAlimonyClaims && alimonyClaimsAmount() > 0 && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Unterhaltszahlungen"}
            fieldname={"pcs_receipt_d"}
            description={"D"}
            helperText={
              "Bitte laden Sie hier Belege hoch, die Ihre Unterhaltszahlungen belegen - zum Beispiel Kontoauszüge. Wenn Ihr Kind eigenes Einkommen hat, laden Sie eine Gehaltsabrechnung Ihres Kindes hoch."
            }
            processCostSupport={product}
          />
        )}

        {product.hasSocialWelfare && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Sozialhilfe"}
            fieldname={"pcs_receipt_social_welfare"}
            description={"Sozialhilfe"}
            helperText={"Bitte laden Sie Ihren aktuellen Bescheid der Sozialhilfe inklusive des Berechnungsbogens hoch."}
            processCostSupport={product}
          />
        )}

        {product.hasIncomeEmployment && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Ihr Einkommen (Angestelltes Arbeitsverhältnis)"}
            fieldname={"pcs_receipt_e1"}
            description={"E1"}
            helperText={"Bitte laden Sie eine aktuelle Lohnabrechnung hoch - mindestens vom letzten Monat, bestenfalls über die letzten 3 Monate."}
            processCostSupport={product}
          />
        )}

        {product.hasIncomeSelfEmployment && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Einkommen aus selbstständiger Arbeit / Gewerbebetrieb oder Land- und Forstwirtschaft"}
            fieldname={"pcs_receipt_e2"}
            description={"E2"}
            helperText={"Bitte laden Sie den letzten Jahresabschluss und/oder letzten Steuerbescheid hoch."}
            processCostSupport={product}
          />
        )}

        {product.hasIncomeRent && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Vermietung / Verpachtung"}
            fieldname={"pcs_receipt_e3"}
            description={"E3"}
            helperText={"Bitte laden Sie den Miet / Pachtvertrag oder einen Kontoauszug mit den monatlichen Mieteingängen hoch."}
            processCostSupport={product}
          />
        )}

        {product.hasIncomeAssets && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Kapitalvermögen"}
            fieldname={"pcs_receipt_e4"}
            description={"E4"}
            helperText={"Bitte laden Sie aktuelle Depotauszüge hoch."}
            processCostSupport={product}
          />
        )}

        {product.hasIncomeLiving && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Wohngeld"}
            fieldname={"pcs_eceipt_e6"}
            description={"E6"}
            helperText={"Bitte laden Sie hier den vollständigen Wohngeldbescheid hoch."}
            processCostSupport={product}
          />
        )}

        {product.hasIncomePension && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Rente/Pension"}
            fieldname={"pcs_receipt_e8"}
            description={"E8"}
            helperText={"Bitte laden Sie den Rentenbescheids vom letzten Monat hoch."}
            processCostSupport={product}
          />
        )}

        {product.hasIncomeUnemploymentAllowance && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Arbeitslosengeld I"}
            fieldname={"pcs_receipt_e9"}
            description={"E9"}
            helperText={
              "Bitte laden Sie hier den aktuellen Bescheid hoch. Falls das Arbeitslosengeld noch nicht bewilligt wurde, laden Sie die Kopie des Antrags oder die Eingangsbestätigung des Antrags beim Arbeitsamt hoch."
            }
            processCostSupport={product}
          />
        )}

        {product.hasIncomeUnemploymentAllowance2 && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Arbeitslosengeld II"}
            fieldname={"pcs_receipt_e10"}
            description={"E10"}
            helperText={
              "Bitte laden Sie hier den aktuellen Bescheid hoch. Falls das Arbeitslosengeld noch nicht bewilligt wurde, laden Sie die Kopie des Antrags oder die Eingangsbestätigung des Antrags beim Arbeitsamt hoch."
            }
            processCostSupport={product}
          />
        )}

        {product.hasIncomeSickness && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Krankengeld"}
            fieldname={"pcs_receipt_e11"}
            description={"E11"}
            helperText={"Bitte laden Sie den aktuellen Bescheid Ihrer Krankenkasse hoch."}
            processCostSupport={product}
          />
        )}

        {product.hasIncomeParentalAllowance && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Elterngeld"}
            fieldname={"pcs_receipt_e12"}
            description={"E12"}
            helperText={"Bitte laden Sie den Bescheid der Familienkasse oder der Agentur für Arbeit hoch."}
            processCostSupport={product}
          />
        )}

        {product.hasIncomeOther1 && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". " + product.incomeOther1Name + " (Sonstige Einnahmen)"}
            fieldname={"pcs_receipt_e13"}
            description={"E13"}
            helperText={"Bitte laden Sie hier Dokumente hoch, die diese Einnahmen belegen."}
            processCostSupport={product}
          />
        )}

        {product.hasIncomeOther2 && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". " + product.incomeOther2Name + " (Sonstige Einnahmen)"}
            fieldname={"pcs_receipt_e14"}
            description={"E14"}
            helperText={"Bitte laden Sie hier Dokumente hoch, die diese Einnahmen belegen."}
            processCostSupport={product}
          />
        )}

        {product.hasOtherInsuranceDeduction && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Versicherungen"}
            fieldname={"pcs_receipt_f3"}
            description={"F3"}
            helperText={"Bitte laden Sie hier einen Kontoauszug über die Zahlungen, die Versicherungspolice oder einen Zahlungsplan hoch."}
            processCostSupport={product}
          />
        )}

        {product.hasMobilityCostDeduction && product.mobilityCostUnit !== "km" && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Fahrkosten"}
            fieldname={"pcs_receipt_f4"}
            description={"F4"}
            helperText={"Bitte laden Sie hier einen Kontoauszug hoch, der die Zahlung für das Fahrticket belegt."}
            processCostSupport={product}
          />
        )}

        {product.hasOtherExpensesDeduction && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Werbekosten / Betriebsausgaben"}
            fieldname={"pcs_receipt_f5"}
            description={"F5"}
            helperText={"Bitte laden Sie hier einen passenden Beleg hoch."}
            processCostSupport={product}
          />
        )}

        {product.hasIncomeLiving === false && (
          <>
            <FileUploadSection
              setIsBlocked={setIsBlocked}
              isBlocked={isBlocked}
              headline={counter++ + ". Wohnkosten"}
              fieldname={"pcs_receipt_h1"}
              description={"H1"}
              helperText={
                product.livingType === "rent"
                  ? "Bitte laden Sie hier Ihren vollständigen Mietvertrag hoch. Sollte Ihnen der Mietvertrag nicht vorliegen, können Sie auch Kontoauszüge hochladen, die Ihre Kosten für die Miete belegen."
                  : "Bitte laden Sie hier die Abrechnungen für Strom, Wasser und Wärme hoch. Sollten Ihnen diese nicht vorliegen, können Sie notfalls auch Kontoauszüge über die Kosten hochladen."
              }
              processCostSupport={product}
            />
            {product.hasPropertyLoans &&
              product.propertyLoans?.map((loan: Record<string, any>, key: number) => {
                return (
                  <FileUploadSection
                    setIsBlocked={setIsBlocked}
                    isBlocked={isBlocked}
                    key={key}
                    headline={counter++ + ". Kreditvertrag für Eigentum vom " + loan.date}
                    fieldname={"pcs_receipt_h6" + (key + 1)}
                    description={"H6" + (key + 1)}
                    helperText={"Laden Sie hier bitte Ihren Darlehensvertrag, Zahlungsplan oder Zahlungsnachweise hoch."}
                    processCostSupport={product}
                  />
                );
              })}
          </>
        )}

        {product.hasOtherPaymentObligations &&
          product.paymentObligations?.map((obligation: Record<string, any>, key: number) => {
            return (
              <FileUploadSection
                setIsBlocked={setIsBlocked}
                isBlocked={isBlocked}
                key={key}
                headline={counter++ + ". " + obligation.name}
                fieldname={"pcs_receipt_i" + (key + 1)}
                description={"I" + (key + 1)}
                helperText={"Laden Sie hier bitte Ihren Darlehensvertrag, Zahlungsplan oder Zahlungsnachweise hoch."}
                processCostSupport={product}
              />
            );
          })}

        {product.hasSpecialFinancialBurdens &&
          product.specialFinancialBurdens?.map((burden: Record<string, any>, key: number) => {
            return (
              <FileUploadSection
                setIsBlocked={setIsBlocked}
                isBlocked={isBlocked}
                key={key}
                headline={counter++ + ". " + burden.description}
                fieldname={"pcs_receipt_j" + (key + 1)}
                description={"J" + (key + 1)}
                helperText={"Bitte belegen Sie die Kosten z.B. durch eine Rechnung oder einen Kontoauszug"}
                processCostSupport={product}
              />
            );
          })}

        {product.hasBankAccount &&
          product.bankAccounts?.map((account: Record<string, any>, key: number) => {
            return (
              <FileUploadSection
                setIsBlocked={setIsBlocked}
                isBlocked={isBlocked}
                key={key}
                headline={counter++ + ". " + account.type + ", " + account.owner + ", " + account.name}
                fieldname={"pcs_receipt_g1" + (key + 1)}
                description={"G1." + (key + 1)}
                helperText={`Bitte laden Sie einen aktuellen Kontoauszug hoch, der genau dem Betrag aus Ihrem Antrag entspricht. Bei Ihnen also ein Beleg über die ${account.amount} €. Ein aktueller Kontoauszug ist Grundvoraussetzung für die Bewilligung von Verfahrenskostenhilfe. Der Kontoauszug muss Folgendes unbedingt enthalten: Name des Kontoinhabers, Datum des Kontoauszugs und aktueller Kontostand von ${account.amount} €.`}
                processCostSupport={product}
              />
            );
          })}

        {product.hasPensionInsurance && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Lebens- oder Rentenversicherung"}
            fieldname={"pcs_receipt_g5"}
            description={"G5"}
            helperText={"Bitte laden Sie hier die schriftliche Bestätigung der Versicherung über den Rückkaufwert hoch."}
            processCostSupport={product}
          />
        )}

        {product.hasOtherAssets && (
          <FileUploadSection
            setIsBlocked={setIsBlocked}
            isBlocked={isBlocked}
            headline={counter++ + ". Sonstige Vermögenswerte"}
            fieldname={"pcs_receipt_g6"}
            description={"G6"}
            helperText={"Bitte laden Sie hier Dokumente hoch, die die Vermögenswerte belegen."}
            processCostSupport={product}
          />
        )}

        <Box sx={receiptsUploadStyle.divider} />

        <Grid container sx={{
          justifyContent: "center"
        }}>
          <Typography variant={"h4"} sx={receiptsUploadStyle.headline}>
            Formular unterschreiben
          </Typography>
          <Grid size={12}>
            <p>
              Ich versichere hiermit, dass meine Angaben vollständig und wahr sind. Das
              <a href={"/documents/Verfahrenskostenhilfe_Hinweisblatt.pdf"} download={"Hinweisblatt.pdf"}>
                {" "}
                Hinweisblatt{" "}
              </a>{" "}
              zu diesem Formular habe ich erhalten und gelesen.
            </p>
            <p>
              Mir ist bekannt, dass unvollständige oder unrichtige Angaben die Aufhebung der Bewilligung von Prozess- oder Verfahrenskostenhilfe und eine
              Strafverfolgung nach sich ziehen können. Das Gericht kann mich auffordern, fehlende Belege nachzureichen und meine Angaben an Eides statt zu
              versichern.
            </p>
            <p>
              Mir ist auch bekannt, dass ich während des Gerichtsverfahrens und innerhalb eines Zeitraums von vier Jahren seit der rechtskräftigen Entscheidung
              oder der sonstigen Beendigung des Verfahrens verpflichtet bin, dem Gericht wesentliche Verbesserungen meiner wirtschaftlichen Lage oder eine
              Änderung meiner Anschrift unaufgefordert und unverzüglich mitzuteilen. Bei laufenden Einkünften ist jede nicht nur einmalige Verbesserung von mehr
              als 100 Euro (brutto) im Monat mitzuteilen. Reduzieren sich geltend gemachte Abzüge, muss ich dies ebenfalls unaufgefordert und unverzüglich
              mitteilen, wenn die Entlastung nicht nur einmalig 100 Euro im Monat übersteigt. Ich weiß, dass die Bewilligung der Prozess- oder
              Verfahrenskostenhilfe bei einem Verstoß gegen diese Pflicht aufgehoben werden kann, und ich dann die gesamten Kosten nachzahlen muss.
            </p>
          </Grid>
          <Grid sx={receiptsUploadStyle.signatureCaption} size={12}>
            <p>Bitte unterzeichnen Sie hier. Ihre Unterschrift wird dann direkt in das Formular eingefügt.</p>
          </Grid>
          <Signature product={product} person={product.petitioner} />
        </Grid>

        <Box sx={receiptsUploadStyle.divider} />

        <Box sx={receiptsUploadStyle.inlineHintBox}>
          <Typography variant={"h4"}>Sie haben alle Belege hochgeladen und unterschrieben?</Typography>
          Dann können Sie die Belege abschicken und unser Kundenservice überprüft Ihren Antrag und die Belege. Sie erhalten zeitnah eine Antwort, ob Ihr Antrag
          eingereicht werden kann, oder noch etwas angepasst werden muss.
        </Box>

        <Box sx={receiptsUploadStyle.buttonContainer}>
          <ButtonLoading color="primary" variant="contained" disabled={!isComplete()} onClick={handleSubmit} isLoading={submitProps.isLoading} fullWidth={true}>
            Jetzt abschicken
          </ButtonLoading>
          {!isComplete() && (
            <Typography sx={{ color: "red", textAlign: "center", marginTop: "1rem" }}>
              Leider liegen noch nicht alle oben geforderten Unterlagen vor, oder Ihre Unterschrift fehlt.
            </Typography>
          )}
        </Box>

        <Box sx={receiptsUploadStyle.inlineHintBox}>
          <Typography variant={"h4"}>Sie haben Fragen zu den Belegen?</Typography>
          Schreiben Sie uns eine Nachricht in Ihrer Servicewelt und wir helfen gerne weiter.
        </Box>
      </TextContainer>
    </Paper>
  );
}
